/**
 * The MEMBER JOINED chat card an assign-task flow was opened from ("Assign a
 * task" button). `chatId` is the chat the card was posted in and `projectId`
 * the project the member joined.
 */
export type AssignTaskSourceCard = {
  messageId: string;
  chatId: string;
  projectId: string;
};

/**
 * TA-11038: assignTaskUsers replaces the MEMBER JOINED card with the TASK
 * ASSIGNED card when given `sourceMessageId`, which the backend only accepts
 * together with `sendCardImmediately`. It rejects the whole assignment when the
 * task is outside the card's project or the chat is a group chat, so the card
 * is only sent for a task in that project in a chat known to be a DM —
 * otherwise (including while the chat is still loading) the task is assigned
 * without the swap. On web the card lives in shared chat-input state that
 * outlives a chat switch, so it is also only sent in the chat it came from.
 */
export const sourceCardAssignVariables = ({
  sourceCard,
  chatId,
  taskProjectId,
  isGroupChat,
}: {
  sourceCard?: AssignTaskSourceCard;
  chatId?: string | null;
  taskProjectId?: string | null;
  isGroupChat?: boolean | null;
}): { sendCardImmediately?: true; sourceMessageId?: string } => {
  if (!sourceCard || isGroupChat !== false) return {};
  if (!chatId || chatId !== sourceCard.chatId) return {};
  if (!taskProjectId || taskProjectId !== sourceCard.projectId) return {};
  return { sendCardImmediately: true, sourceMessageId: sourceCard.messageId };
};

/**
 * TA-11120: createTask from a DM sends the TASK ASSIGNED card itself, so it
 * takes the card to replace as `sourceMessageId` too. Same guards as
 * {@link sourceCardAssignVariables}; `sendCardImmediately` is left out because
 * a createTask with `chatId` already sends its card immediately.
 */
export const sourceCardCreateVariables = (
  args: Parameters<typeof sourceCardAssignVariables>[0]
): { sourceMessageId?: string } => {
  const { sourceMessageId } = sourceCardAssignVariables(args);
  return sourceMessageId ? { sourceMessageId } : {};
};
